import EventBusyIcon from '@mui/icons-material/EventBusy'
import ApartmentIcon from '@mui/icons-material/Apartment'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { Alert, Box, Button, CircularProgress, FormControl, InputAdornment, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type React from 'react'
import { generatePath, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EmptyState } from 'Components/EmptyState'
import {
  AttendanceFilterBar,
  AttendanceFilterSelect,
  AttendancePageShell,
  AttendanceSessionCard
} from 'Pages/Attendance/AttendanceComponents'
import {
  activeDayIndexes,
  allBlocksValue,
  allFacilitiesValue,
  allStaffValue,
  attendanceDayLabels,
  dateRangeLabel,
  optionValue,
  rateLabel,
  scheduleLabel,
  sessionStatus,
  staffFilterOptions,
  staffLabel,
  timeLabel,
  uniqueOptions
} from 'Pages/Attendance/attendanceDashboardFormatters'
import { AttendanceSessionRoutePlaceholder } from 'Pages/Attendance/AttendanceSessionPage'
import { type AttendanceAccountContext, type AttendanceDashboardSessionDto } from 'Pages/Attendance/types'
import { AccountType } from 'core/types'
import { useAttendanceAccountContext } from 'Pages/Attendance/useAttendanceAccountContext'
import { defaultAttendanceDashboardFilters, type AttendanceDashboardFilters, type AttendanceDashboardState, useAttendanceDashboard } from 'Pages/Attendance/useAttendanceDashboard'
import * as AppRoutes from 'Pages/Routes'

export function AttendancePage (): JSX.Element {
  const accountContext = useAttendanceAccountContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<AttendanceDashboardFilters>(defaultAttendanceDashboardFilters)
  const attendanceYear = attendanceYearFromSearchParams(searchParams)
  const providerSearch = searchParams.get('providerSearch') ?? ''
  const debouncedProviderSearch = useDebouncedValue(providerSearch, 250)
  const isAdminOrISD = accountContext.accountType === AccountType.ISD || accountContext.accountType === AccountType.Admin
  const dashboardState = useAttendanceDashboard(
    accountContext,
    filters,
    isAdminOrISD ? { ...attendanceYear, providerSearch: debouncedProviderSearch } : {}
  )
  const setAttendanceYear = (option: AttendanceYearOption): void => {
    updateSearchParams(searchParams, setSearchParams, params => {
      setAttendanceYearParam(params, option)
    })
  }
  const setProviderSearch = (value: string): void => {
    updateSearchParams(searchParams, setSearchParams, params => {
      setOptionalSearchParam(params, 'providerSearch', value)
      resetTablePageParam(params, 'provider')
    })
  }
  const onSessionAction = (sessionId: number): void => {
    navigate(generatePath(AppRoutes.ATTENDANCE_SESSION, { sessionId: String(sessionId) }))
  }

  if (isAdminOrISD) {
    return (
      <AttendanceProviderDrilldownView
        accountContext={accountContext}
        dashboardState={dashboardState}
        attendanceYear={attendanceYear}
        onAttendanceYearChange={setAttendanceYear}
        searchText={providerSearch}
        onSearchTextChange={setProviderSearch}
      />
    )
  }

  return (
    <AttendanceSessionsView
      accountContext={accountContext}
      dashboardState={dashboardState}
      filters={filters}
      onFiltersChange={setFilters}
      onSessionAction={onSessionAction}
    />
  )
}

export function AttendanceSessionsView (props: {
  accountContext: AttendanceAccountContext
  dashboardState: AttendanceDashboardState
  filters?: AttendanceDashboardFilters
  onFiltersChange?: (filters: AttendanceDashboardFilters) => void
  onSessionAction?: (sessionId: number) => void
  sessionsOverride?: AttendanceDashboardSessionDto[]
  shellTitle?: string
  shellSubtitle?: string
  shellEyebrow?: React.ReactNode
  shellActions?: React.ReactNode
  shellToolbar?: React.ReactNode
}): JSX.Element {
  const [localFilters, setLocalFilters] = useState<AttendanceDashboardFilters>(defaultAttendanceDashboardFilters)
  const filters = props.filters ?? localFilters
  const setFilters = (nextFilters: AttendanceDashboardFilters): void => {
    if (props.onFiltersChange === undefined) {
      setLocalFilters(nextFilters)
      return
    }

    props.onFiltersChange(nextFilters)
  }

  const sessions = props.sessionsOverride ?? props.dashboardState.data?.sessions ?? []
  const blockOptions = useMemo(() => [
    { value: allBlocksValue, label: 'All blocks' },
    ...uniqueOptions(sessions, session => optionValue(session.sessionBlockId, session.sessionBlockName ?? 'Unassigned block'))
  ], [sessions])
  const facilityOptions = useMemo(() => [
    { value: allFacilitiesValue, label: 'All facilities' },
    ...uniqueOptions(sessions, session => ({ value: String(session.facilityId), label: session.facilityName }))
  ], [sessions])
  const staffOptions = useMemo(() => [
    { value: allStaffValue, label: 'All staff' },
    ...uniqueOptions(sessions.flatMap(staffFilterOptions), option => option)
  ], [sessions])
  const shellActions = props.shellActions ?? (
    <AttendanceFilterBar>
      <AttendanceFilterSelect
        id="attendance-block-filter"
        label="Block"
        value={filters.blockFilter}
        options={blockOptions}
        onChange={blockFilter => { setFilters({ ...filters, blockFilter }) }}
      />
      <AttendanceFilterSelect
        id="attendance-facility-filter"
        label="Facility"
        value={filters.facilityFilter}
        options={facilityOptions}
        onChange={facilityFilter => { setFilters({ ...filters, facilityFilter }) }}
      />
      <AttendanceFilterSelect
        id="attendance-staff-filter"
        label="Staff"
        value={filters.staffFilter}
        options={staffOptions}
        onChange={staffFilter => { setFilters({ ...filters, staffFilter }) }}
      />
    </AttendanceFilterBar>
  )

  return (
    <AttendancePageShell
      title={props.shellTitle ?? 'Session Attendance'}
      subtitle={props.shellSubtitle}
      eyebrow={props.shellEyebrow}
      accountContext={props.accountContext}
      actions={shellActions}
      toolbar={props.shellToolbar}
    >
      {props.dashboardState.loading && sessions.length === 0 && (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 4 }} role="status">
          <CircularProgress size={24} />
          <Typography>Loading session attendance.</Typography>
        </Box>
      )}

      {props.dashboardState.error.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {props.dashboardState.error.join(' ')}
        </Alert>
      )}

      {!props.dashboardState.loading && props.dashboardState.error.length === 0 && sessions.length === 0 && (
        <EmptyState
          icon={filters.blockFilter === allBlocksValue && filters.facilityFilter === allFacilitiesValue && filters.staffFilter === allStaffValue ? <EventBusyIcon /> : <SearchOffIcon />}
          title={filters.blockFilter === allBlocksValue && filters.facilityFilter === allFacilitiesValue && filters.staffFilter === allStaffValue ? 'No attendance sessions are available.' : 'No sessions match the selected filters.'}
          description={filters.blockFilter === allBlocksValue && filters.facilityFilter === allFacilitiesValue && filters.staffFilter === allStaffValue ? 'Adjust your attendance access.' : 'Adjust the block, facility, or staff filter to broaden the session list.'}
        />
      )}

      {props.dashboardState.error.length === 0 && sessions.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          {props.dashboardState.loading && (
            <Box
              role="status"
              sx={{
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.72)',
                borderRadius: 1,
                display: 'flex',
                gap: 1.5,
                inset: 0,
                justifyContent: 'center',
                minHeight: 96,
                position: 'absolute',
                zIndex: 1
              }}
            >
              <CircularProgress size={24} />
              <Typography>Refreshing session attendance.</Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: '20px',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
              opacity: props.dashboardState.loading ? 0.55 : 1
            }}
          >
            {sessions.map(session => {
              const status = sessionStatus(session)
              const scheduledDayIndexes = activeDayIndexes(session.scheduledDays)
              return (
                <AttendanceSessionCard
                  key={session.sessionId}
                  name={session.sessionName}
                  facility={session.facilityName}
                  staff={staffLabel(session)}
                  schedule={scheduleLabel(session.scheduledDays)}
                  dateRange={dateRangeLabel(session.startDate, session.endDate)}
                  dateRangeNote={session.dateRangeNote ?? undefined}
                  days={attendanceDayLabels.map((label, index) => ({ label, active: scheduledDayIndexes.includes(index) }))}
                  time={timeLabel(session.startTime, session.endTime)}
                  statusLabel={status.label}
                  statusTone={status.tone}
                  statusTooltip={session.missingRecordCountIsUnbounded === true
                    ? 'Missing records cannot be calculated because this session does not have a start date.'
                    : undefined}
                  metrics={[
                    {
                      label: 'Overall Attendance Rate',
                      value: rateLabel(session.attendanceRate),
                      tooltip: session.attendanceRate == null
                        ? 'No attendance records have been entered for this session.'
                        : undefined
                    }
                  ]}
                  disabled={props.dashboardState.loading}
                  onAction={props.onSessionAction === undefined ? undefined : () => { props.onSessionAction?.(session.sessionId) }}
                />
              )
            })}
          </Box>
        </Box>
      )}
    </AttendancePageShell>
  )
}

interface AttendanceProviderGroup {
  districtName: string
  providerId: number
  providerName: string
  isdName: string
  facilityCount: number
  studentCount: number
  incompleteCount: number
  attendanceRate?: number | null
  tardyRate?: number | null
  completionRate?: number | null
}

interface AttendanceFacilityGroup {
  districtId: number
  districtName: string
  providerId: number
  providerName: string
  facilityId: number
  facilityName: string
  sessionCount: number
  studentCount: number
  incompleteCount: number
  attendanceRate?: number | null
  tardyRate?: number | null
  completionRate?: number | null
}

interface AttendanceTableColumn<T> {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  width?: number | string
  render: (row: T) => React.ReactNode
  sortValue?: (row: T) => string | number | null | undefined
}

interface AttendanceYearOption {
  label: string
  from: string
  to: string
}

type AttendanceTableStateKey = 'provider' | 'facility'

const attendanceYearStartParam = 'yearStart'
const defaultRowsPerPage = 15
const tableRowsPerPageOptions = [15, 25, 50]
const tableQueryKeys: Record<AttendanceTableStateKey, { page: string, rowsPerPage: string, sort: string, direction: string }> = {
  provider: { page: 'providerPage', rowsPerPage: 'providerRows', sort: 'providerSort', direction: 'providerDir' },
  facility: { page: 'facilityPage', rowsPerPage: 'facilityRows', sort: 'facilitySort', direction: 'facilityDir' }
}

export function AttendanceProviderFacilitiesRoute (): JSX.Element {
  const accountContext = useAttendanceAccountContext()
  const isAdminOrISD = accountContext.accountType === AccountType.ISD || accountContext.accountType === AccountType.Admin

  if (!isAdminOrISD) {
    return <AttendancePage />
  }

  return <AttendanceProviderFacilitiesISDRoute accountContext={accountContext} />
}

function AttendanceProviderFacilitiesISDRoute (props: { accountContext: AttendanceAccountContext }): JSX.Element {
  const { providerId } = useParams()
  const routeProviderId = routeId(providerId)

  if (routeProviderId === undefined) {
    return <InvalidAttendanceDrilldownView accountContext={props.accountContext} onBackPath={AppRoutes.ATTENDANCE} />
  }

  return <AttendanceProviderFacilitiesISDRouteContent accountContext={props.accountContext} providerId={routeProviderId} />
}

function AttendanceProviderFacilitiesISDRouteContent (props: { accountContext: AttendanceAccountContext, providerId: string }): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const attendanceYear = attendanceYearFromSearchParams(searchParams)
  const dashboardState = useAttendanceDashboard(props.accountContext, defaultAttendanceDashboardFilters, { providerId: props.providerId, ...attendanceYear })
  const setAttendanceYear = (option: AttendanceYearOption): void => {
    updateSearchParams(searchParams, setSearchParams, params => {
      setAttendanceYearParam(params, option)
    })
  }

  return (
    <AttendanceFacilityDrilldownView
      accountContext={props.accountContext}
      dashboardState={dashboardState}
      providerId={props.providerId}
      attendanceYear={attendanceYear}
      onAttendanceYearChange={setAttendanceYear}
      onBack={() => {
        navigate(withSearch(AppRoutes.ATTENDANCE, drilldownSearch(location.search, 'provider')))
      }}
    />
  )
}

export function AttendanceProviderFacilitySessionsRoute (): JSX.Element {
  const accountContext = useAttendanceAccountContext()
  const isAdminOrISD = accountContext.accountType === AccountType.ISD || accountContext.accountType === AccountType.Admin

  if (!isAdminOrISD) {
    return <AttendancePage />
  }

  return <AttendanceFacilitySessionsISDRoute accountContext={accountContext} />
}

function AttendanceFacilitySessionsISDRoute (props: { accountContext: AttendanceAccountContext }): JSX.Element {
  const { providerId, facilityId } = useParams()
  const routeProviderId = routeId(providerId)
  const routeFacilityId = routeId(facilityId)

  if (routeProviderId === undefined || routeFacilityId === undefined) {
    return <InvalidAttendanceDrilldownView accountContext={props.accountContext} onBackPath={AppRoutes.ATTENDANCE} />
  }

  return <AttendanceProviderFacilitySessionsISDRouteContent accountContext={props.accountContext} providerId={routeProviderId} facilityId={routeFacilityId} />
}

function AttendanceProviderFacilitySessionsISDRouteContent (props: { accountContext: AttendanceAccountContext, providerId: string, facilityId: string }): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const attendanceYear = attendanceYearFromSearchParams(searchParams)
  const dashboardState = useAttendanceDashboard(props.accountContext, defaultAttendanceDashboardFilters, { providerId: props.providerId, facilityId: props.facilityId, ...attendanceYear })
  const setAttendanceYear = (option: AttendanceYearOption): void => {
    updateSearchParams(searchParams, setSearchParams, params => {
      setAttendanceYearParam(params, option)
    })
  }

  return (
    <AttendanceSessionDrilldownView
      accountContext={props.accountContext}
      dashboardState={dashboardState}
      providerId={props.providerId}
      facilityId={props.facilityId}
      attendanceYear={attendanceYear}
      onAttendanceYearChange={setAttendanceYear}
      onBack={() => {
        navigate(withSearch(generatePath(AppRoutes.ATTENDANCE_PROVIDER_FACILITIES, {
          providerId: props.providerId
        }), drilldownSearch(location.search, 'facility')))
      }}
    />
  )
}

function AttendanceProviderDrilldownView (props: {
  accountContext: AttendanceAccountContext
  dashboardState: AttendanceDashboardState
  attendanceYear: AttendanceYearOption
  onAttendanceYearChange: (option: AttendanceYearOption) => void
  searchText: string
  onSearchTextChange: (value: string) => void
}): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const sessions = props.dashboardState.data?.sessions ?? []
  const providers = useMemo(() => providerGroups(sessions), [sessions])

  return (
    <AttendancePageShell
      title="Attendance"
      titleSuffix={props.accountContext.scopeLabel}
      accountContext={props.accountContext}
      toolbar={
        <AttendanceISDControls
          searchLabel="Search by Provider Name"
          searchValue={props.searchText}
          onSearchChange={props.onSearchTextChange}
          attendanceYear={props.attendanceYear}
          onAttendanceYearChange={props.onAttendanceYearChange}
        />
      }
    >
      <AttendanceDrilldownContent
        loading={props.dashboardState.loading}
        error={props.dashboardState.error}
        empty={providers.length === 0}
        emptyTitle={props.searchText.trim() === '' ? 'No attendance providers are available.' : 'No providers match the search.'}
        emptyDescription={props.searchText.trim() === '' ? 'No attendance sessions are available for the selected ISD context.' : 'Adjust the provider search to broaden the list.'}
      >
        <AttendanceTable
          label="Attendance providers"
          columns={providerTableColumns()}
          rows={providers}
          stateKey="provider"
          rowKey={provider => provider.providerId}
          onRowClick={provider => {
            navigate(withSearch(generatePath(AppRoutes.ATTENDANCE_PROVIDER_FACILITIES, {
              providerId: String(provider.providerId)
            }), drilldownSearch(location.search, 'provider')))
          }}
        />
      </AttendanceDrilldownContent>
    </AttendancePageShell>
  )
}

function InvalidAttendanceDrilldownView (props: { accountContext: AttendanceAccountContext, onBackPath: string }): JSX.Element {
  const navigate = useNavigate()

  return (
    <AttendancePageShell
      title="Attendance"
      subtitle="The attendance drilldown link is invalid."
      accountContext={props.accountContext}
      eyebrow={<AttendanceBackButton onClick={() => { navigate(props.onBackPath) }}>Back to Attendance</AttendanceBackButton>}
    >
      <Alert severity="error" sx={{ mb: 2 }}>
        The selected attendance drilldown is invalid. Return to Attendance and select a provider or facility again.
      </Alert>
    </AttendancePageShell>
  )
}

function AttendanceDrilldownContent (props: {
  loading: boolean
  error: string[]
  empty: boolean
  emptyTitle: string
  emptyDescription: string
  children: React.ReactNode
}): JSX.Element {
  if (props.loading && props.empty) {
    return (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 4 }} role="status">
        <CircularProgress size={24} />
        <Typography>Loading session attendance.</Typography>
      </Box>
    )
  }

  if (props.error.length > 0) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {props.error.join(' ')}
      </Alert>
    )
  }

  if (props.empty) {
    return (
      <EmptyState
        icon={<EventBusyIcon />}
        title={props.emptyTitle}
        description={props.emptyDescription}
      />
    )
  }

  if (props.loading) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box
          role="status"
          sx={{
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
            borderRadius: 1,
            display: 'flex',
            gap: 1.5,
            inset: 0,
            justifyContent: 'center',
            minHeight: 96,
            position: 'absolute',
            zIndex: 1
          }}
        >
          <CircularProgress size={24} />
          <Typography>Refreshing session attendance.</Typography>
        </Box>
        <Box sx={{ opacity: 0.55, pointerEvents: 'none' }}>
          {props.children}
        </Box>
      </Box>
    )
  }

  return <>{props.children}</>
}

function AttendanceBackButton (props: { children: React.ReactNode, onClick: () => void }): JSX.Element {
  return (
    <Button
      variant="text"
      size="small"
      startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '0.875rem' }} />}
      onClick={props.onClick}
      sx={{
        color: '#595959',
        fontFamily: 'Quicksand, sans-serif',
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'none'
      }}
    >
      {props.children}
    </Button>
  )
}

function AttendanceSessionDrilldownView (props: {
  accountContext: AttendanceAccountContext
  dashboardState: AttendanceDashboardState
  providerId: string
  facilityId: string
  attendanceYear: AttendanceYearOption
  onAttendanceYearChange: (option: AttendanceYearOption) => void
  onBack: () => void
}): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const selectedProviderId = Number(props.providerId)
  const selectedFacilityId = Number(props.facilityId)
  const sessions = (props.dashboardState.data?.sessions ?? [])
    .filter(session => session.providerId === selectedProviderId && session.facilityId === selectedFacilityId)
  const selectedSession = sessions[0]

  return (
    <AttendanceSessionsView
      accountContext={props.accountContext}
      dashboardState={props.dashboardState}
      sessionsOverride={sessions}
      shellTitle={selectedSession?.facilityName ?? 'Sessions'}
      shellSubtitle={selectedSession?.providerName ?? 'Select a session to view attendance records.'}
      shellEyebrow={<AttendanceBackButton onClick={props.onBack}>Back to Schools</AttendanceBackButton>}
      shellActions={<></>}
      shellToolbar={
        <AttendanceISDControls
          attendanceYear={props.attendanceYear}
          onAttendanceYearChange={props.onAttendanceYearChange}
        />
      }
      onSessionAction={sessionId => {
        const attendanceBackPath = generatePath(AppRoutes.ATTENDANCE_PROVIDER_FACILITY_SESSIONS, {
          providerId: props.providerId,
          facilityId: props.facilityId
        })
        const sessionPath = generatePath(AppRoutes.ATTENDANCE_SESSION, { sessionId: String(sessionId) })
        const attendanceBackPathWithSearch = withSearch(attendanceBackPath, location.search)

        navigate(`${sessionPath}?returnTo=${encodeURIComponent(attendanceBackPathWithSearch)}`, {
          state: {
            attendanceBackPath: attendanceBackPathWithSearch
          }
        })
      }}
    />
  )
}

function AttendanceFacilityDrilldownView (props: {
  accountContext: AttendanceAccountContext
  dashboardState: AttendanceDashboardState
  providerId: string
  attendanceYear: AttendanceYearOption
  onAttendanceYearChange: (option: AttendanceYearOption) => void
  onBack: () => void
}): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const selectedProviderId = Number(props.providerId)
  const sessions = props.dashboardState.data?.sessions ?? []
  const facilities = useMemo(() => facilityGroups(sessions, selectedProviderId), [sessions, selectedProviderId])
  const matchingSession = sessions.find(session => session.providerId === selectedProviderId)
  const providerName = facilities[0]?.providerName ?? matchingSession?.providerName ?? 'Provider'
  const providerSessions = useMemo(() => sessions.filter(session => session.providerId === selectedProviderId), [sessions, selectedProviderId])
  const districtName = districtSummary(providerSessions)

  return (
    <AttendancePageShell
      title={providerName}
      subtitle={districtName}
      accountContext={props.accountContext}
      eyebrow={<AttendanceBackButton onClick={props.onBack}>Back to Providers</AttendanceBackButton>}
      toolbar={
        <AttendanceISDControls
          attendanceYear={props.attendanceYear}
          onAttendanceYearChange={props.onAttendanceYearChange}
        />
      }
    >
      <AttendanceDrilldownContent
        loading={props.dashboardState.loading}
        error={props.dashboardState.error}
        empty={facilities.length === 0}
        emptyTitle="No schools are available for this provider."
        emptyDescription="No attendance sessions are available for the selected provider."
      >
        <AttendanceTable
          label="Attendance schools"
          columns={facilityTableColumns()}
          rows={facilities}
          stateKey="facility"
          rowKey={facility => facility.facilityId}
          onRowClick={facility => {
            navigate(withSearch(generatePath(AppRoutes.ATTENDANCE_PROVIDER_FACILITY_SESSIONS, {
              providerId: String(facility.providerId),
              facilityId: String(facility.facilityId)
            }), drilldownSearch(location.search, 'facility')))
          }}
        />
      </AttendanceDrilldownContent>
    </AttendancePageShell>
  )
}

function AttendanceTable<T> (props: {
  label: string
  columns: Array<AttendanceTableColumn<T>>
  rows: T[]
  stateKey: AttendanceTableStateKey
  rowKey: (row: T) => string | number
  onRowClick?: (row: T) => void
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryKeys = tableQueryKeys[props.stateKey]
  const defaultSortKey = props.columns[0]?.key ?? ''
  const requestedSortKey = searchParams.get(queryKeys.sort) ?? defaultSortKey
  const sortKey = props.columns.some(column => column.key === requestedSortKey) ? requestedSortKey : defaultSortKey
  const sortDirection: 'asc' | 'desc' = searchParams.get(queryKeys.direction) === 'desc' ? 'desc' : 'asc'
  const requestedRowsPerPage = Number(searchParams.get(queryKeys.rowsPerPage))
  const rowsPerPage = tableRowsPerPageOptions.includes(requestedRowsPerPage) ? requestedRowsPerPage : defaultRowsPerPage
  const requestedPage = parseTablePage(searchParams.get(queryKeys.page))
  const sortedRows = useMemo(() => {
    const sortColumn = props.columns.find(column => column.key === sortKey)
    if (sortColumn?.sortValue === undefined) {
      return props.rows
    }

    return [...props.rows].sort((a, b) => {
      const comparison = compareSortValues(sortColumn.sortValue?.(a), sortColumn.sortValue?.(b))
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [props.columns, props.rows, sortDirection, sortKey])
  const lastPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1)
  const page = Math.min(requestedPage, lastPage)
  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  useEffect(() => {
    if (requestedPage !== page) {
      updateSearchParams(searchParams, setSearchParams, params => {
        setTablePageParam(params, props.stateKey, page)
      })
    }
  }, [page, props.stateKey, requestedPage, searchParams, setSearchParams])

  const updateSort = (column: AttendanceTableColumn<T>): void => {
    if (column.sortValue === undefined) {
      return
    }

    if (column.key === sortKey) {
      updateSearchParams(searchParams, setSearchParams, params => {
        setSearchParam(params, queryKeys.direction, sortDirection === 'asc' ? 'desc' : 'asc', 'asc')
        setTablePageParam(params, props.stateKey, 0)
      })
      return
    }

    updateSearchParams(searchParams, setSearchParams, params => {
      setSearchParam(params, queryKeys.sort, column.key, defaultSortKey)
      setSearchParam(params, queryKeys.direction, 'asc', 'asc')
      setTablePageParam(params, props.stateKey, 0)
    })
  }
  const handleRowKeyDown = (row: T) => (event: React.KeyboardEvent<HTMLTableRowElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      props.onRowClick?.(row)
    }
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          overflowX: 'auto'
        }}
      >
        <Table aria-label={props.label} sx={{ backgroundColor: '#FFFFFF', minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ height: 50 }}>
              {props.columns.map(column => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    borderBottom: '1px solid #E1E1E1',
                    color: '#4A4A4A',
                    fontFamily: 'Quicksand, sans-serif',
                    fontSize: 14,
                    fontWeight: 700,
                    px: '12px',
                    py: '16px',
                    width: column.width
                  }}
                >
                  {column.sortValue === undefined
                    ? column.label
                    : (
                      <TableSortLabel
                        active={sortKey === column.key}
                        direction={sortKey === column.key ? sortDirection : 'asc'}
                        onClick={() => { updateSort(column) }}
                      >
                        {column.label}
                      </TableSortLabel>
                      )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map(row => (
              <TableRow
                key={props.rowKey(row)}
                hover
                role={props.onRowClick === undefined ? undefined : 'button'}
                tabIndex={props.onRowClick === undefined ? undefined : 0}
                onClick={props.onRowClick === undefined ? undefined : () => { props.onRowClick?.(row) }}
                onKeyDown={props.onRowClick === undefined ? undefined : handleRowKeyDown(row)}
                sx={{
                  cursor: props.onRowClick === undefined ? 'default' : 'pointer',
                  height: 50,
                  '&:hover': {
                    backgroundColor: '#EFEFEF'
                  }
                }}
              >
                {props.columns.map(column => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sx={{
                      borderBottom: '1px solid #E1E1E1',
                      color: '#4A4A4A',
                      fontFamily: 'Quicksand, sans-serif',
                      fontSize: 14,
                      fontWeight: 600,
                      px: '12px',
                      py: '9px',
                      verticalAlign: 'middle'
                    }}
                  >
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedRows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={tableRowsPerPageOptions}
        onPageChange={(_, nextPage) => {
          updateSearchParams(searchParams, setSearchParams, params => {
            setTablePageParam(params, props.stateKey, nextPage)
          })
        }}
        onRowsPerPageChange={event => {
          updateSearchParams(searchParams, setSearchParams, params => {
            setSearchParam(params, queryKeys.rowsPerPage, event.target.value, String(defaultRowsPerPage))
            setTablePageParam(params, props.stateKey, 0)
          })
        }}
        sx={{
          color: '#4A4A4A',
          fontFamily: 'Quicksand, sans-serif',
          '.MuiTablePagination-toolbar': {
            minHeight: 46
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontFamily: 'Quicksand, sans-serif',
            fontSize: 12
          }
        }}
      />
    </Box>
  )
}

function providerTableColumns (): Array<AttendanceTableColumn<AttendanceProviderGroup>> {
  return [
    {
      key: 'provider',
      label: 'Provider',
      width: '40%',
      render: provider => (
        <AttendanceNameCell icon={<ApartmentIcon sx={{ fontSize: 16 }} />} primary={provider.providerName} secondary={provider.districtName} />
      ),
      sortValue: provider => provider.providerName
    },
    { key: 'facilities', label: '# Schools', align: 'right', width: 139, render: provider => provider.facilityCount, sortValue: provider => provider.facilityCount },
    { key: 'students', label: '# Students', align: 'right', width: 127, render: provider => provider.studentCount, sortValue: provider => provider.studentCount },
    { key: 'attendanceRate', label: 'Attendance Rate', align: 'right', width: 165, render: provider => rateLabel(provider.attendanceRate), sortValue: provider => provider.attendanceRate },
    { key: 'tardyRate', label: 'Tardy Rate', align: 'right', width: 138, render: provider => rateLabel(provider.tardyRate), sortValue: provider => provider.tardyRate },
    { key: 'status', label: 'Attendance Status', width: 172, render: provider => <AttendanceCompletionStatus completionRate={provider.completionRate} />, sortValue: provider => provider.completionRate }
  ]
}

function facilityTableColumns (): Array<AttendanceTableColumn<AttendanceFacilityGroup>> {
  return [
    {
      key: 'facility',
      label: 'School',
      width: '40%',
      render: facility => (
        <AttendanceNameCell icon={<ApartmentIcon sx={{ fontSize: 16 }} />} primary={facility.facilityName} secondary={facility.districtName} />
      ),
      sortValue: facility => facility.facilityName
    },
    { key: 'sessions', label: '# Sessions', align: 'right', width: 139, render: facility => facility.sessionCount, sortValue: facility => facility.sessionCount },
    { key: 'students', label: '# Students', align: 'right', width: 127, render: facility => facility.studentCount, sortValue: facility => facility.studentCount },
    { key: 'attendanceRate', label: 'Attendance Rate', align: 'right', width: 165, render: facility => rateLabel(facility.attendanceRate), sortValue: facility => facility.attendanceRate },
    { key: 'tardyRate', label: 'Tardy Rate', align: 'right', width: 138, render: facility => rateLabel(facility.tardyRate), sortValue: facility => facility.tardyRate },
    { key: 'status', label: 'Attendance Status', width: 172, render: facility => <AttendanceCompletionStatus completionRate={facility.completionRate} />, sortValue: facility => facility.completionRate }
  ]
}

function AttendanceISDControls (props: {
  searchLabel?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  attendanceYear: AttendanceYearOption
  onAttendanceYearChange: (option: AttendanceYearOption) => void
}): JSX.Element {
  const yearOptions = useMemo(() => attendanceYearOptions(), [])

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: '21px',
        justifyContent: props.searchLabel === undefined ? 'flex-end' : 'space-between',
        width: '100%'
      }}
    >
      {props.searchLabel !== undefined && props.searchValue !== undefined && props.onSearchChange !== undefined && (
        <Box sx={{ width: { xs: '100%', sm: 350 } }}>
          <Typography
            component="label"
            htmlFor="attendance-isd-search"
            sx={{ color: '#8F8F8F', display: 'block', fontFamily: 'Quicksand, sans-serif', fontSize: 14, fontWeight: 500, mb: '4px' }}
          >
            {props.searchLabel}
          </Typography>
          <TextField
            id="attendance-isd-search"
            value={props.searchValue}
            onChange={event => { props.onSearchChange?.(event.target.value) }}
            size="small"
            inputProps={{ 'aria-label': props.searchLabel }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: '#4A4A4A', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                color: '#4A4A4A',
                fontFamily: 'Quicksand, sans-serif',
                fontSize: 14,
                height: 44
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#B2B2B4'
              }
            }}
          />
        </Box>
      )}
      <Box sx={{ width: { xs: '100%', sm: 180 } }}>
        <Typography
          component="label"
          id="attendance-year-label"
          sx={{ color: '#8F8F8F', display: 'block', fontFamily: 'Quicksand, sans-serif', fontSize: 14, fontWeight: 500, mb: '4px' }}
        >
          Date Range
        </Typography>
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            labelId="attendance-year-label"
            value={props.attendanceYear.label}
            onChange={(event: SelectChangeEvent) => {
              const selectedOption = yearOptions.find(option => option.label === event.target.value)
              if (selectedOption !== undefined) {
                props.onAttendanceYearChange(selectedOption)
              }
            }}
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              color: '#4A4A4A',
              fontFamily: 'Quicksand, sans-serif',
              fontSize: 14,
              fontWeight: 600,
              height: 44,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#B2B2B4'
              }
            }}
          >
            {yearOptions.map(option => (
              <MenuItem key={option.label} value={option.label}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  )
}

function AttendanceNameCell (props: { icon: React.ReactNode, primary: string, secondary?: string }): JSX.Element {
  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: '10px', minWidth: 0 }}>
      <Box sx={{ alignItems: 'center', backgroundColor: '#7136BC', borderRadius: '50%', color: '#FFFFFF', display: 'flex', flex: '0 0 32px', height: 32, justifyContent: 'center', width: 32 }}>
        {props.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: '#4A4A4A', fontFamily: 'Quicksand, sans-serif', fontSize: 14, fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {props.primary}
        </Typography>
        {props.secondary !== undefined && props.secondary.trim() !== '' && (
          <Typography sx={{ color: '#595959', fontFamily: 'Quicksand, sans-serif', fontSize: 12, fontWeight: 500, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {props.secondary}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function AttendanceCompletionStatus (props: { completionRate?: number | null }): JSX.Element {
  const isUpToDate = (props.completionRate ?? 0) > 0.5
  const color = isUpToDate ? '#3694BC' : '#BC3656'
  const label = props.completionRate == null
    ? 'No Data'
    : isUpToDate
      ? '> 50% Up To Date'
      : '<= 50% Up To Date'

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: isUpToDate ? 'rgba(54, 148, 188, 0.1)' : 'rgba(188, 54, 86, 0.1)',
        borderRadius: '20px',
        color,
        display: 'inline-flex',
        fontFamily: 'Quicksand, sans-serif',
        fontSize: 10,
        fontWeight: 600,
        gap: '6px',
        height: 22,
        justifyContent: 'center',
        minWidth: 121,
        px: '10px',
        whiteSpace: 'nowrap'
      }}
    >
      <Box sx={{ backgroundColor: color, borderRadius: '50%', height: 8, width: 8 }} />
      {label}
    </Box>
  )
}

function providerGroups (sessions: AttendanceDashboardSessionDto[]): AttendanceProviderGroup[] {
  const groups = new Map<number, AttendanceDashboardSessionDto[]>()
  sessions.forEach(session => {
    groups.set(session.providerId, [...(groups.get(session.providerId) ?? []), session])
  })

  return Array.from(groups.values())
    .map(groupSessions => {
      const first = groupSessions[0]
      return {
        districtName: districtSummary(groupSessions),
        providerId: first.providerId,
        providerName: first.providerName,
        isdName: first.isdName,
        facilityCount: new Set(groupSessions.map(session => session.facilityId)).size,
        studentCount: groupSessions.reduce((total, session) => total + session.enrolledCount, 0),
        incompleteCount: incompleteSessionCount(groupSessions),
        attendanceRate: groupAttendanceRate(groupSessions),
        tardyRate: groupTardyRate(groupSessions),
        completionRate: groupCompletionRate(groupSessions)
      }
    })
    .sort((a, b) => a.providerName.localeCompare(b.providerName))
}

function facilityGroups (sessions: AttendanceDashboardSessionDto[], providerId: number): AttendanceFacilityGroup[] {
  const providerSessions = sessions.filter(session => session.providerId === providerId)
  const groups = new Map<number, AttendanceDashboardSessionDto[]>()
  providerSessions.forEach(session => {
    groups.set(session.facilityId, [...(groups.get(session.facilityId) ?? []), session])
  })

  return Array.from(groups.values())
    .map(groupSessions => {
      const first = groupSessions[0]
      return {
        districtId: first.districtId ?? 0,
        districtName: first.districtName ?? 'Unknown District',
        providerId: first.providerId,
        providerName: first.providerName,
        facilityId: first.facilityId,
        facilityName: first.facilityName,
        sessionCount: groupSessions.length,
        studentCount: groupSessions.reduce((total, session) => total + session.enrolledCount, 0),
        incompleteCount: incompleteSessionCount(groupSessions),
        attendanceRate: groupAttendanceRate(groupSessions),
        tardyRate: groupTardyRate(groupSessions),
        completionRate: groupCompletionRate(groupSessions)
      }
    })
    .sort((a, b) => a.facilityName.localeCompare(b.facilityName))
}

function districtSummary (sessions: AttendanceDashboardSessionDto[]): string {
  const districtNames = uniqueOptions(sessions, session => optionValue(session.districtId, session.districtName ?? 'Unknown District'))
    .map(option => option.label)

  if (districtNames.length === 0) {
    return ''
  }

  if (districtNames.length === 1) {
    return districtNames[0]
  }

  return `${districtNames.length} districts`
}

function incompleteSessionCount (sessions: AttendanceDashboardSessionDto[]): number {
  return sessions.filter(session => session.status === 'Incomplete' || session.status === 'UnknownMissingCount').length
}

function groupAttendanceRate (sessions: AttendanceDashboardSessionDto[]): number | null {
  const recordedCount = sessions.reduce((total, session) => total + session.recordedCount, 0)
  if (recordedCount === 0) {
    return null
  }

  const attendedCount = sessions.reduce((total, session) => total + session.presentCount + session.tardyCount, 0)
  return attendedCount / recordedCount
}

function groupTardyRate (sessions: AttendanceDashboardSessionDto[]): number | null {
  const recordedCount = sessions.reduce((total, session) => total + session.recordedCount, 0)
  if (recordedCount === 0) {
    return null
  }

  const tardyCount = sessions.reduce((total, session) => total + session.tardyCount, 0)
  return tardyCount / recordedCount
}

function groupCompletionRate (sessions: AttendanceDashboardSessionDto[]): number | null {
  const recordedCount = sessions.reduce((total, session) => total + session.recordedCount, 0)
  const missingCount = sessions.reduce((total, session) => total + (session.missingRecordCount ?? 0), 0)
  const expectedCount = recordedCount + missingCount

  if (expectedCount === 0 || sessions.some(session => session.missingRecordCountIsUnbounded === true)) {
    return null
  }

  return recordedCount / expectedCount
}

function compareSortValues (aValue: string | number | null | undefined, bValue: string | number | null | undefined): number {
  if (aValue == null && bValue == null) {
    return 0
  }

  if (aValue == null) {
    return 1
  }

  if (bValue == null) {
    return -1
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue
  }

  return String(aValue).localeCompare(String(bValue))
}

function attendanceYearOptions (): AttendanceYearOption[] {
  const currentOption = currentAttendanceYearOption()
  const currentStartYear = Number(currentOption.from.substring(0, 4))
  return [1, 0, -1].map(offset => attendanceYearOption(currentStartYear + offset))
}

function currentAttendanceYearOption (): AttendanceYearOption {
  const today = new Date()
  const startYear = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1
  return attendanceYearOption(startYear)
}

function attendanceYearOption (startYear: number): AttendanceYearOption {
  const endYear = startYear + 1
  return {
    label: `${startYear} - ${String(endYear).slice(-2)}`,
    from: `${startYear}-07-01`,
    to: `${endYear}-06-30`
  }
}

function attendanceYearFromSearchParams (searchParams: URLSearchParams): AttendanceYearOption {
  const startYear = Number(searchParams.get(attendanceYearStartParam))
  if (Number.isInteger(startYear) && startYear >= 2000 && startYear <= 2100) {
    return attendanceYearOption(startYear)
  }

  return currentAttendanceYearOption()
}

function setAttendanceYearParam (searchParams: URLSearchParams, option: AttendanceYearOption): void {
  setSearchParam(searchParams, attendanceYearStartParam, option.from.substring(0, 4), currentAttendanceYearOption().from.substring(0, 4))
}

function updateSearchParams (
  currentSearchParams: URLSearchParams,
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  update: (nextSearchParams: URLSearchParams) => void
): void {
  const nextSearchParams = new URLSearchParams(currentSearchParams)
  update(nextSearchParams)
  setSearchParams(nextSearchParams, { replace: true })
}

function setOptionalSearchParam (searchParams: URLSearchParams, key: string, value: string): void {
  const normalizedValue = value.trim()
  if (normalizedValue === '') {
    searchParams.delete(key)
    return
  }

  searchParams.set(key, value)
}

function setSearchParam (searchParams: URLSearchParams, key: string, value: string, defaultValue: string): void {
  if (value === defaultValue) {
    searchParams.delete(key)
    return
  }

  searchParams.set(key, value)
}

function resetTablePageParam (searchParams: URLSearchParams, stateKey: AttendanceTableStateKey): void {
  searchParams.delete(tableQueryKeys[stateKey].page)
}

function setTablePageParam (searchParams: URLSearchParams, stateKey: AttendanceTableStateKey, page: number): void {
  setSearchParam(searchParams, tableQueryKeys[stateKey].page, String(page), '0')
}

function parseTablePage (page: string | null): number {
  if (page == null) {
    return 0
  }

  const parsedPage = Number(page)
  return Number.isInteger(parsedPage) && parsedPage >= 0 ? parsedPage : 0
}

function withSearch (path: string, search: string): string {
  return search === '' ? path : `${path}${search}`
}

function drilldownSearch (search: string, scope: 'provider' | 'facility'): string {
  const currentSearchParams = new URLSearchParams(search)
  const nextSearchParams = new URLSearchParams()
  const providerKeys = [attendanceYearStartParam, 'providerSearch', ...Object.values(tableQueryKeys.provider)]
  const facilityKeys = Object.values(tableQueryKeys.facility)
  const keys = scope === 'provider' ? providerKeys : [...providerKeys, ...facilityKeys]

  keys.forEach(key => {
    const value = currentSearchParams.get(key)
    if (value != null) {
      nextSearchParams.set(key, value)
    }
  })

  const nextSearch = nextSearchParams.toString()
  return nextSearch === '' ? '' : `?${nextSearch}`
}

function useDebouncedValue<T> (value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [value, delay])

  return debouncedValue
}

function routeId (value?: string, allowZero = false): string | undefined {
  if (value === undefined) {
    return undefined
  }

  const numericValue = Number(value)
  return Number.isInteger(numericValue) && (allowZero ? numericValue >= 0 : numericValue > 0) ? value : undefined
}

export { AttendanceSessionRoutePlaceholder }
